import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { MEMORY_CARDS, shuffleCards } from '@/mocks/organes-memory-data';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

type Level = 6 | 10 | 14 | 20;

export default function MemoryGame() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors } = useGame();
  const [level, setLevel] = useState<Level | null>(null);
  const [cards, setCards] = useState(shuffleCards(MEMORY_CARDS.slice(0, 12)));
  const [flipped, setFlipped] = useState<number[]>([]);
  const [matched, setMatched] = useState<number[]>([]);
  const [checking, setChecking] = useState(false);
  const [showFact, setShowFact] = useState<string | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);
  const [moves, setMoves] = useState(0);

  const handleStart = (l: Level) => {
    setLevel(l);
    const selected = shuffleCards(MEMORY_CARDS.slice(0, l));
    setCards(selected);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setShowGameOver(false);
    startGame('memory');
  };

  const handleCardClick = useCallback((cardId: number) => {
    if (checking || flipped.includes(cardId) || matched.includes(cardId)) return;

    const newFlipped = [...flipped, cardId];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setChecking(true);
      setMoves(m => m + 1);
      const [first, second] = newFlipped;
      const card1 = cards.find(c => c.id === first);
      const card2 = cards.find(c => c.id === second);

      if (card1 && card2 && card1.pairId === card2.pairId) {
        const newMatched = [...matched, first, second];
        setMatched(newMatched);
        const points = 50;
        updateScore(gameScore + points);
        setShowFact(card1.fact);
        setTimeout(() => setShowFact(null), 3000);
        setFlipped([]);
        setChecking(false);

        if (newMatched.length === cards.length) {
          const bonus = Math.max(0, 300 - moves * 5);
          const finalScore = gameScore + points + bonus;
          updateScore(finalScore);
          setTimeout(() => {
            endGame(finalScore, gameTime, gameErrors);
            setShowGameOver(true);
          }, 800);
        }
      } else {
        updateErrors(gameErrors + 1);
        setTimeout(() => {
          setFlipped([]);
          setChecking(false);
        }, 800);
      }
    }
  }, [flipped, matched, checking, cards, gameScore, gameErrors, gameTime, moves, updateScore, updateErrors, endGame]);

  const handleReplay = () => {
    if (level) handleStart(level);
  };

  const handleQuit = () => {
    endGame(gameScore, gameTime, gameErrors);
    navigate('/jeux', { replace: true });
  };

  if (!level) {
    return (
      <GameWrapper gameName="Memory Medical" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-brain-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Memory Medical</h2>
            <p className="text-foreground-600 text-sm mb-6">Choisis ton niveau :</p>
            <div className="grid grid-cols-2 gap-3">
              {([6, 10, 14, 20] as Level[]).map((l) => (
                <button
                  key={l}
                  onClick={() => handleStart(l)}
                  className="py-3 px-4 bg-secondary-100 hover:bg-secondary-200 rounded-xl text-sm font-medium text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {l} cartes {l <= 10 ? '(Facile)' : l <= 14 ? '(Moyen)' : '(Difficile)'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </GameWrapper>
    );
  }

  const cols = cards.length <= 12 ? 'grid-cols-3 sm:grid-cols-4' : 'grid-cols-4 sm:grid-cols-5';

  return (
    <GameWrapper gameName="Memory Medical" onQuit={handleQuit}>
      <div className="flex-1 p-4 max-w-3xl mx-auto w-full flex flex-col">
        <div className="text-sm text-foreground-600 mb-4 text-center">
          Coups : {moves} | Paires : {matched.length / 2} / {cards.length / 2}
        </div>

        {showFact && (
          <div className="bg-accent-100/70 border border-accent-200/50 rounded-xl p-3 mb-4 text-sm text-accent-900 text-center animate-in">
            <i className="ri-lightbulb-line mr-1.5"></i> {showFact}
          </div>
        )}

        <div className={`grid ${cols} gap-2 md:gap-3 flex-1 content-start`}>
          {cards.map((card) => {
            const isFlipped = flipped.includes(card.id);
            const isMatched = matched.includes(card.id);
            const show = isFlipped || isMatched;

            return (
              <button
                key={card.id}
                onClick={() => handleCardClick(card.id)}
                disabled={isMatched}
                className={`aspect-square rounded-xl flex items-center justify-center transition-all duration-300 cursor-pointer ${
                  isMatched
                    ? 'bg-green-100 border-2 border-green-300 scale-95'
                    : show
                      ? 'bg-accent-100 border-2 border-accent-300 scale-105'
                      : 'bg-background-100 border-2 border-background-200 hover:border-primary-500/50 hover:bg-primary-50'
                }`}
              >
                {show ? (
                  <div className="text-center p-1">
                    <i className={`${card.icon} text-xl md:text-3xl ${isMatched ? 'text-green-600' : 'text-accent-600'}`}></i>
                    <span className="block text-[10px] md:text-xs mt-1 font-medium text-foreground-950">{card.name}</span>
                  </div>
                ) : (
                  <i className="ri-question-mark text-xl md:text-2xl text-foreground-400"></i>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="memory"
          message="Mémoire d&apos;éléphant !"
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}